import Vue from "vue";
import Vuex from "vuex";

import user from "./user.js";
import news from "./news.js";
import goals from "./goals";
import downloads from "./downloads";
import connectedUsers from "./connectedUsers.js";
import newsCategories from "./modulesNewsCategories/newsCategories";
import helpdesk from "./helpdesk.js";
import applications from "./Applications.js";
import policies from "./Policies.js";
import chat from "./chat.js";
import ManagementPermission from "./ManagementPermission.js";
import Occupation from "./Occupation.js";
import polls from "./polls.js";
import competitions from "./Competitions.js";
import comments from "./commentsContest.js";
import MonthEmployee from "./MonthEmployee.js";
import platforms from "./platforms.js";
import skipNewsHome from "./skipNewsHome.js";

import axios from "@/api/axios";
import { isCoorSpecial } from "@/js/validation";

import restVuex from "./base/newRestVuex.js";
import { httpCrud, appApiCrud } from "./base/apiStrategies.js";

import publicApi from "@/api/publicApi";

Vue.use(Vuex);

export default new Vuex.Store({
  modules: {
    cities: restVuex("cities", {
      api: httpCrud({ client: publicApi, basePath: "/cities" }),
      selectList: (resp) => resp.data?.cities || resp.data || [],
      selectUpdate: (resp) => resp.data?.city || resp.data,
    }),

    offices: restVuex("offices", {
      api: appApiCrud("offices"),
      selectList: (resp) => resp.data?.offices || resp.data || [],
      selectUpdate: (resp) => resp.data?.office || resp.data,
    }),

    rooms: restVuex("rooms", {
      api: appApiCrud("rooms"),
      selectList: (resp) => resp.data?.rooms || resp.data || [],
      selectUpdate: (resp) => resp.data?.room || resp.data,
    }),

    users: restVuex("users", {
      api: appApiCrud("users"),
      selectList: (resp) => resp.data?.users || resp.data || [],
      selectUpdate: (resp) => resp.data?.user || resp.data,
      extend: {
        state: { selectsOptionsMonitors: [] },
        actions: {
          async fetchUsers({ dispatch }, query) {
            return dispatch("fetch", query);
          },
          async fetchMonitors({ commit, getters }) {
            commit("setLoading", true);
            try {
              const resp = await axios.get("/users?monitorSelect=true");
              const list = (resp?.data?.users ?? []).filter((u) => u.role === "monitor" || isCoorSpecial(u));

              commit("setSelectsOptionsMonitors", list);
              commit("setLoaded", true);
              commit("setLoading", false);

              return getters.getSelectsOptionsMonitors;
            } catch (e) {
              const err = e?.response?.data?.error || e?.message || "Error inesperado";
              commit("setError", err);
              return [];
            }
          },
        },
        mutations: {
          setSelectsOptionsMonitors(state, list) {
            state.selectsOptionsMonitors = Array.isArray(list) ? list : [];
          },
        },
        getters: {
          getModels: (s) => s.data.filter((x) => x.role === "model"),
          getMonitors: (s) => {
            const source = Array.isArray(s.data) && s.data.length ? s.data : s.selectsOptionsMonitors;
            return source.filter((x) => x.role === "monitor" || isCoorSpecial(x));
          },
          getSelectsOptionsMonitors: (s) => [
            { value: "", text: "Seleccione...", disabled: true },
            ...s.selectsOptionsMonitors
              .filter((u) => u?.isEnable)
              .map((u) => ({ value: u._id, text: u.user }))
              .sort((a, b) => (a.text?.toLowerCase() > b.text?.toLowerCase() ? 1 : -1)),
          ],
        },
      },
    }),

    news,
    user,
    goals,
    connectedUsers,
    downloads,
    newsCategories,
    helpdesk,
    chat,
    applications,
    ManagementPermission,
    policies,
    Occupation,
    polls,
    competitions,
    comments,
    MonthEmployee,
    platforms,
    skipNewsHome,
  },

  state: () => ({
    isDesktopApp: localStorage.getItem("isDesktopApp") || false,
    hasUpdateSW: false,
    screen: { width: window.innerWidth, height: window.innerHeight, scrollTop: 0, scrollDirection: "down" },
    scrollKey: 0,
    appMenu: localStorage.getItem("appMenu") || "",
    appMenuMobile: null,
    countCases: 0,
    localIPs: [],
    companies: [],
  }),

  actions: {
    initEvents({ state }) {
      window.removeEventListener("scroll", isScrolling, true);
      window.addEventListener("scroll", isScrolling, true);

      let setScrollBuffer = null;

      function isScrolling() {
        const scrolledToBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight;

        if (scrolledToBottom) {
          const elms = document.querySelectorAll(".click-on-bottom");
          elms.forEach((el) => el.click());
        }

        const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;

        clearTimeout(setScrollBuffer);

        setScrollBuffer = setTimeout(() => {
          if (windowScrollTop > state.screen.scrollTop) {
            state.screen.scrollDirection = "down";
          } else {
            state.screen.scrollDirection = "up";
          }

          state.screen.scrollTop = windowScrollTop;
          state.scrollKey++;
        }, 100);
      }

      function onMessage(evt) {
        if (evt.data.msg === "updateSW") {
          window.caches
            .keys()
            .then(function (names) {
              for (let name of names) caches.delete(name);
            })
            .catch((error) => {
              console.log("PopUpdateSW", error);
            });
          document.querySelector(".filterBlur")?.classList.add("blur");
          state.hasUpdateSW = true;
        }

        if (evt.data.message === "isDesktopApp") {
          localStorage.setItem("isDesktopApp", "true");
          state.isDesktopApp = true;
          state.user.localIPs = evt.data.localIPs;
        }
      }

      window.removeEventListener("message", onMessage);
      window.addEventListener("message", onMessage);
      window.addEventListener("resize", function () {
        let width = window.innerWidth;
        let height = window.innerHeight;
        state.screen.width = width;
        state.screen.height = height;
      });
    },

    async getCases({ state }) {
      if (localStorage.getItem(process.env.VUE_APP_TOKEN_NAME)) {
        const { data } = await axios.get(`/helpdesk/number`);
        state.countCases = data?.total || 0;
      }
    },

    async setItemViewed(_, item) {
      try {
        await this.$axios.post(`views?item=${item}`);
      } catch (error) {
        console.log(error);
      }
    },

    async getItemViewersTotal(_, item) {
      try {
        const { data } = await this.$axios.get(`views/totals?item=${item}`);
        return data?.totalViewers || 0;
      } catch (error) {
        console.log(error);
      }
    },

    async getItemViewers(_, { item, userId = "", page = "" }) {
      try {
        const { data } = await this.$axios.get(`views?item=${item}&page=${page}&userId=${userId}`);
        const { viewers = [], hasMore = false, error } = data;

        if (!error) {
          return data;
        }
        return { viewers, hasMore };
      } catch (error) {
        console.log(error);
      }
    },

    async getCompanies({ commit }) {
      try {
        const { data } = await this.$axios("/company?status=active");
        commit("setCompanies", data?.companies?.companies);
      } catch (error) {
        console.log("error :>> ", error);
      }
    },
  },

  mutations: {
    setCompanies(state, payload = []) {
      state.companies = payload;
    },
  },

  getters: {
    screen: (state) => state.screen,
    listCompanies: (state) => state.companies,
  },
});
